import React, { useState } from 'react';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import Select from '../../../components/ui/select';

const AttendanceFilters = ({
  onFiltersChange,
  onExport,
  totalRecords,
  isUnknownFilter,
  onIsUnknownFilterChange,
  activeTab,
  timeFilter,
  handleTimeFilterChange,
  employeeFilters,
  onEmployeeFilterChange
}) => {
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    employeeSearch: '',
    department: '',
    status: '',
    cameraLocation: '',
    isUnknown: ''
  });
  const isUnknownOptions = [
    { value: '', label: 'All Employees' },
    { value: 'known', label: 'Known Employees' },
    { value: 'unknown', label: 'Unknown Employees' },
  ];
  const [isExpanded, setIsExpanded] = useState(false);

  const departmentOptions = [
    { value: '', label: 'All Departments' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'operations', label: 'Operations' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'present', label: 'Present' },
    { value: 'late', label: 'Late' },
    { value: 'absent', label: 'Absent' },
    { value: 'half-day', label: 'Half Day' },
    { value: 'overtime', label: 'Overtime' }
  ];

  const cameraLocationOptions = [
    { value: '', label: 'All Locations' },
    { value: 'main-entrance', label: 'Main Entrance' },
    { value: 'side-entrance', label: 'Side Entrance' },
    { value: 'parking-gate', label: 'Parking Gate' },
    { value: 'cafeteria', label: 'Cafeteria' },
    { value: 'floor-2', label: 'Floor 2' },
    { value: 'floor-3', label: 'Floor 3' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      dateFrom: '',
      dateTo: '',
      employeeSearch: '',
      department: '',
      status: '',
      cameraLocation: ''
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  return (
    <div className="space-y-3 mb-6">
      {/* Filter Toggle Button */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="group relative flex items-center gap-2 px-4 py-2.5 text-sm bg-white rounded-lg hover:shadow-lg transition-all duration-300 border border-gray-100  transform hover:-translate-y-0.5"
        >
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          {/* Animated circle decoration */}
          <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-700"></div>

          <div className="relative z-10 flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
              <Icon name="Filter" size={16} />
            </div>
            <span className="font-medium text-gray-700 group-hover:text-blue-600 transition-colors duration-300">
              Show Filters
            </span>
            {hasActiveFilters && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                Active
              </span>
            )}
          </div>
        </button>
      )}

      {/* Animated Filter Panel */}
      <div
        className={`
    overflow-visible transition-all duration-500 ease-out
    ${isExpanded ? "max-h-[1000px] opacity-100 translate-y-0" : "max-h-0 opacity-0 -translate-y-4"}
  `}
      >

        {isExpanded && (
          <div className="relative bg-white rounded-xl p-4 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-visible group">

            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-purple-50/30 to-pink-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

            {/* Decorative circles */}
            <div className="absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-10 group-hover:scale-150 transition-transform duration-1000"></div>
            <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full opacity-10 group-hover:scale-150 transition-transform duration-1000"></div>

            <div className="relative z-10 space-y-4">
              {/* Header with record count */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Filter" size={18} className="text-blue-600" />
                  <span className="text-sm font-semibold text-gray-700">Filters</span>
                </div>
                <span className="text-xs text-gray-500 font-medium">
                  {totalRecords} records
                </span>
              </div>

              {/* All Tab Filters */}
              {activeTab === "all" && (
                <>
                  {/* Quick Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                    <div className="transform transition-all duration-300 hover:scale-[1.02]">
                      <Input
                        type="text"
                        placeholder="Search employees..."
                        value={filters?.employeeSearch}
                        onChange={(e) => handleFilterChange('employeeSearch', e?.target?.value)}
                        className="h-9 text-sm border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                      />
                    </div>

                    <div className="flex items-center gap-1 transform transition-all duration-300 hover:scale-[1.02]">
                      <Input
                        type="date"
                        value={filters?.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                        className="h-9 text-xs border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all duration-300"
                      />
                      <span className="text-xs text-gray-400 font-medium px-1">to</span>
                      <Input
                        type="date"
                        value={filters?.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                        className="h-9 text-xs border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all duration-300"
                      />
                    </div>

                    <div className="transform transition-all duration-300 hover:scale-[1.02]">
                      <Select
                        placeholder="Select status"
                        options={statusOptions}
                        value={filters?.status}
                        onChange={(value) => handleFilterChange('status', value)}
                        className="h-9 text-sm border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                      />
                    </div>

                    <div className="transform transition-all duration-300 hover:scale-[1.02]">
                      <Select
                        placeholder="Select location"
                        options={cameraLocationOptions}
                        value={filters?.cameraLocation}
                        onChange={(value) => handleFilterChange('cameraLocation', value)}
                        className="h-9 text-sm border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 transition-all duration-300"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Employee Tab - Time Period Filters */}
              {activeTab === 'employee' && (
                <div className="bg-gradient-to-br from-blue-50/50 to-purple-50/50 p-3 rounded-lg border border-blue-100">
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Time Period</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleTimeFilterChange('today')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 transform hover:scale-105 ${timeFilter === 'today'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-white text-gray-600 hover:bg-blue-50 border border-gray-200'
                        }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => handleTimeFilterChange('week')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 transform hover:scale-105 ${timeFilter === 'week'
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-white text-gray-600 hover:bg-purple-50 border border-gray-200'
                        }`}
                    >
                      This Week
                    </button>
                    <button
                      onClick={() => handleTimeFilterChange('month')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 transform hover:scale-105 ${timeFilter === 'month'
                          ? 'bg-pink-600 text-white shadow-md'
                          : 'bg-white text-gray-600 hover:bg-pink-50 border border-gray-200'
                        }`}
                    >
                      This Month
                    </button>
                    <button
                      onClick={() => handleTimeFilterChange('year')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 transform hover:scale-105 ${timeFilter === 'year'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-white text-gray-600 hover:bg-blue-50 border border-gray-200'
                        }`}
                    >
                      This Year
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="text-xs text-gray-500 italic">
                  Use filters to narrow down results
                </div>
                <div className="flex items-center gap-2">
                  {hasActiveFilters && (
                    <button
                      onClick={handleClearFilters}
                      className="group/btn h-9 px-3 text-xs text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1.5 transition-all duration-300 transform hover:scale-105"
                    >
                      <Icon name="X" size={14} className="group-hover/btn:rotate-90 transition-transform duration-300" />
                      Clear
                    </button>
                  )}

                  <button
                    onClick={() => setIsExpanded(false)}
                    className="h-9 px-2 text-gray-500 hover:text-pink-600 hover:bg-pink-50 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <Icon name="ChevronUp" size={16} className="hover:translate-y-0.5 transition-transform duration-300" />
                  </button>
                </div>
              </div>

              {/* Active Filter Tags */}
              {hasActiveFilters && (
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100">
                  {filters.employeeSearch && (
                    <FilterTag
                      label={`Search: ${filters.employeeSearch}`}
                      onRemove={() => handleFilterChange('employeeSearch', '')}
                      color="blue"
                    />
                  )}
                  {filters.dateFrom && (
                    <FilterTag
                      label={`From: ${filters.dateFrom}`}
                      onRemove={() => handleFilterChange('dateFrom', '')}
                      color="purple"
                    />
                  )}
                  {filters.dateTo && (
                    <FilterTag
                      label={`To: ${filters.dateTo}`}
                      onRemove={() => handleFilterChange('dateTo', '')}
                      color="purple"
                    />
                  )}
                  {filters.status && (
                    <FilterTag
                      label={`Status: ${filters.status}`}
                      onRemove={() => handleFilterChange('status', '')}
                      color="blue"
                    />
                  )}
                  {filters.cameraLocation && (
                    <FilterTag
                      label={`Location: ${filters.cameraLocation}`}
                      onRemove={() => handleFilterChange('cameraLocation', '')}
                      color="pink"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Animated pulse dots */}
            <div className="absolute top-3 right-3 flex space-x-1">
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse"></div>
              <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '75ms' }}></div>
              <div className="w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FilterTag = ({ label, onRemove, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200",
    purple: "bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200",
    pink: "bg-pink-50 hover:bg-pink-100 text-pink-700 border-pink-200"
  };

  return (
    <span
      className={`group/tag inline-flex items-center gap-1.5 ${colorClasses[color]} border text-xs px-2.5 py-1.5 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-sm`}
    >
      <span className="max-w-[150px] truncate font-medium">{label}</span>
      <button
        onClick={onRemove}
        className="text-current opacity-60 hover:opacity-100 transition-all duration-200 transform hover:rotate-90"
      >
        <Icon name="X" size={12} />
      </button>
    </span>
  );
};

export default AttendanceFilters;